'use client'

import SvgIcon from '@mui/material/SvgIcon'
import { alpha } from '@mui/material/styles'
import { ReactElement, ReactNode } from 'react'
import { tss } from 'tss-react/mui'

const ARROW_SIZE = 16

interface InnerBubbleProps {
    inverted?: boolean
    isSkeleton?: boolean
    showArrow?: boolean
    pending?: boolean
    children: ReactNode
}

const useStyles = tss
    .withName('Bubble')
    .withNestedSelectors<'inverted' | 'isSkeleton' | 'isPending'>()
    .create(({ theme, classes }) => ({
        root: {
            position: 'relative',
            padding: theme.spacing(1),
            paddingLeft: theme.spacing(2),
            paddingTop: theme.spacing(1.5),
            marginRight: '20%',
            marginLeft: `${ARROW_SIZE}px`,
            borderRadius: theme.shape.borderRadius * 2,
            backgroundColor: theme.palette.grey[200],
            boxShadow: theme.shadows[2],
            wordBreak: 'break-word',

            '& a': {
                textDecoration: 'underline',
            },

            [`&.${classes.inverted}`]: {
                marginLeft: '20%',
                marginRight: `${ARROW_SIZE}px`,
                color: theme.palette.common.white,
                backgroundColor: theme.palette.info.main,
                boxShadow: theme.shadows[1],

                '& a': {
                    color: theme.palette.common.white,
                    // textDecoration rule above doesn't apply for some reason, have
                    // to repeat here.
                    textDecoration: 'underline',
                },

                [`&.${classes.isPending}`]: {
                    color: '#8eb7d0',
                },
            },

            [`&.${classes.isSkeleton}`]: {
                padding: 0,
                paddingTop: 0,
                paddingLeft: 0,
                backgroundColor: 'transparent',
                boxShadow: 'none',
            },
        },
        svg: {
            color: theme.palette.grey[200],
            position: 'absolute',
            top: 16,
            left: -ARROW_SIZE,
            width: ARROW_SIZE,
            height: ARROW_SIZE,

            [`&.${classes.inverted}`]: {
                left: 'auto',
                right: -ARROW_SIZE,
                color: theme.palette.info.main,
                transform: 'scale(-1, 1)',
            },

            [`&.${classes.isSkeleton}`]: {
                color: alpha(theme.palette.text.primary, 0.11),
            },
        },
        inverted: {},
        isSkeleton: {},
        isPending: {},
    }))

function Bubble({ isSkeleton, inverted, showArrow = true, pending, children }: InnerBubbleProps): ReactElement {
    const { classes, cx } = useStyles()

    return (
        <div
            className={cx(
                classes.root,
                inverted && classes.inverted,
                pending && classes.isPending,
                isSkeleton && classes.isSkeleton
            )}
        >
            {children}
            {showArrow ? (
                <SvgIcon
                    viewBox={`0 0 ${ARROW_SIZE} ${ARROW_SIZE}`}
                    className={cx(classes.svg, inverted && classes.inverted, isSkeleton && classes.isSkeleton)}
                >
                    <path d="M0.6315445,3.10237889 L16,15.9980993 L16,0 L1.8937008,0 C0.8478387,0 0,0.795749125 0,1.77735542 C0,2.2832603 0.2297026,2.76519203 0.6315445,3.10237889 Z" />
                </SvgIcon>
            ) : null}
        </div>
    )
}

export default Bubble
